/**
 * 메뉴디자이너 API — BE /api/design/v2 (web/aigateway/design). 2026-09-29.
 *
 * 초안 읽기 · 쓰기 · 템플릿 · 미리보기 · 반영만 여기다. 대화는 없다 —
 * AI Bot 창(chatApi.sendChatMessageV2)이 디자이너 화면이 열려 있으면 이슈번호를 실어 /api/chat/v2 로 보내고,
 * 제안이 오면 notifyDesignChanged 로 알린다. 디자이너 화면은 subscribeDesignChanged 로 초안을 다시 읽는다.
 * 행은 정의 테이블 칼럼 이름 그대로(sys_formctrl · sys_formgridcolumn · sys_formlayout).
 */
import { apiFetch } from "@shared/api/api";

/** 디자이너 화면 메뉴코드. AI Bot 이 이 메뉴가 활성일 때 이슈번호를 같이 보낸다 */
export const DESIGNER_MENU = "SN_SFA_ISSUE_REG_007";

type DesignChangedListener = (noIssue: string) => void;
const designChangedListeners = new Set<DesignChangedListener>();

/** 디자이너 화면이 구독한다. 돌려주는 함수로 해지 */
export function subscribeDesignChanged(listener: DesignChangedListener): () => void {
  designChangedListeners.add(listener);
  return () => {
    designChangedListeners.delete(listener);
  };
}

/** AI Bot 이 제안(designProposal)을 받았다 — BE 가 초안에 이미 저장했으니 화면은 다시 읽기만 한다 */
export function notifyDesignChanged(noIssue: string) {
  designChangedListeners.forEach((l) => {
    try {
      l(noIssue);
    } catch {}
  });
}

/* AI Bot 이 말을 보내기 직전에 디자이너 화면의 안 저장된 것을 먼저 저장하게 한다 (2026-09-30).
   AI 는 저장된 초안을 보므로, 저장을 잊은 채 말하면 고치기 전 상태를 본다 — 그 엇갈림을 막는다 */
const designFlushers = new Map<string, () => Promise<void>>();

/** 디자이너 화면이 자기 이슈번호로 등록한다. 돌려주는 함수로 해지 */
export function registerDesignFlush(noIssue: string, flush: () => Promise<void>): () => void {
  designFlushers.set(noIssue, flush);
  return () => {
    if (designFlushers.get(noIssue) === flush) designFlushers.delete(noIssue);
  };
}

/** 등록된 저장을 부른다. 실패해도 대화는 이어 간다 */
export async function flushDesignDraft(noIssue: string): Promise<void> {
  const f = designFlushers.get(noIssue);
  if (!f) return;
  try {
    await f();
  } catch {}
}

export type DefRow = Record<string, any>;

/** BE DesignDraft 와 같은 모양 */
export interface DesignDraft {
  noIssue: string;
  cdMenu?: string | null;
  nmMenu?: string | null;
  fgMenu?: string | null;
  fgDesign?: string | null;
  cdTemplate?: string | null;
  stDesign?: string | null;
  controls: DefRow[];
  gridColumns: DefRow[];
  queries: Record<string, string>;
  layout: DefRow[];
}

export interface DesignTemplate {
  cd_menu: string;
  nm_title: string;
  fg_menu: "REG" | "REP";
}

export const MAIN_QUERY = "main_01";

export async function fetchDesignTemplates(): Promise<DesignTemplate[]> {
  const res = await apiFetch<DesignTemplate[]>("/api/design/v2/templates", { method: "GET" });
  return res.data ?? [];
}

/** 메뉴(또는 템플릿 코드)의 영역 배치 — 공용 페이지 · 템플릿 미리보기 */
export async function fetchDesignLayout(cdMenu: string): Promise<DefRow[]> {
  const res = await apiFetch<DefRow[]>(`/api/design/v2/layout/${encodeURIComponent(cdMenu)}`, { method: "GET" });
  return res.data ?? [];
}

export async function loadDesignDraft(noIssue: string): Promise<DesignDraft> {
  const res = await apiFetch<DesignDraft>(`/api/design/v2/${encodeURIComponent(noIssue)}`, { method: "GET" });
  return res.data as DesignDraft;
}

export async function chooseDesignTemplate(noIssue: string, cdTemplate: string, nmMenu?: string): Promise<DesignDraft> {
  const res = await apiFetch<DesignDraft>(`/api/design/v2/${encodeURIComponent(noIssue)}/template`, {
    method: "POST",
    body: { cdTemplate, nmMenu } as unknown as BodyInit,
  });
  return res.data as DesignDraft;
}

/** 초안 초기화 — 이슈의 초안을 지운다. 배포된 이슈는 메뉴 연결만 남는다. (DELETE 는 BE 요청 래퍼가 못 받아 POST) */
export async function resetDesignDraft(noIssue: string): Promise<DesignDraft> {
  const res = await apiFetch<DesignDraft>(`/api/design/v2/${encodeURIComponent(noIssue)}/reset`, {
    method: "POST",
    body: {} as unknown as BodyInit,
  });
  return res.data as DesignDraft;
}

export async function saveDesignDraft(noIssue: string, draft: DesignDraft): Promise<DesignDraft> {
  const res = await apiFetch<DesignDraft>(`/api/design/v2/${encodeURIComponent(noIssue)}`, {
    method: "PUT",
    body: draft as unknown as BodyInit,
  });
  return res.data as DesignDraft;
}

/** 초안 쿼리 미리보기 — getDataSet 과 같은 모양(data[0], data[1] …). 사람이 버튼을 눌러야 부른다 */
export async function previewDesignQuery(noIssue: string, cdQuery: string, params: Record<string, any>): Promise<DefRow[][]> {
  const res = await apiFetch<DefRow[][]>(`/api/design/v2/${encodeURIComponent(noIssue)}/preview`, {
    method: "POST",
    body: { cdQuery, params } as unknown as BodyInit,
  });
  return res.data ?? [];
}

/** 배포 전 점검 결과 — BE AiDesignService.DeployCheck 와 같은 모양 */
export interface DeployCheck {
  synced: boolean;
  leaf: boolean;
  /** CUSTOM 확실히 사용자 정의 · UNKNOWN 화면 파일 이름이 있음 — 소스가 실제로 있는지는 FE 가 hasScreenModule 로 가른다 */
  kind: "UNKNOWN" | "CUSTOM" | null;
  nmMenu: string | null;
  nmFile: string | null;
  deployed: boolean;
  defaultQueryId: string | null;
  queryUsedByOther: boolean;
}

/** 메뉴를 고른 순간 점검 — 동기화 · 말단 · 패키지/사용자 정의 · 이미 배포됨 · 쿼리 이름 충돌 */
export async function deployCheck(cdMenu: string, cdQuery?: string): Promise<DeployCheck> {
  const q = cdQuery ? `?cdQuery=${encodeURIComponent(cdQuery)}` : "";
  const res = await apiFetch<DeployCheck>(`/api/design/v2/deploy-check/${encodeURIComponent(cdMenu)}${q}`, { method: "GET" });
  return res.data as DeployCheck;
}

/** 배포 — 초안을 본 테이블로. 메뉴가 바뀌면 옛 메뉴 정의는 서버가 지운다 (결정 21) */
export async function deployDesignDraft(noIssue: string, cdMenu: string, cdQuery: string): Promise<DesignDraft> {
  const res = await apiFetch<DesignDraft>(`/api/design/v2/${encodeURIComponent(noIssue)}/deploy`, {
    method: "POST",
    body: { cdMenu, cdQuery } as unknown as BodyInit,
  });
  return res.data as DesignDraft;
}
